import { useEffect, useRef, useCallback } from 'react';
import { useTutorChatStore } from '../store/useTutorChatStore';
import { useUIStore } from '../store';
import { useGroupListStore } from '../store/useGroupListStore';
import { useChatHistoryQuery } from './queries/useChatHistoryQuery';
import { Mode } from '../types/tutor.types';

/**
 * useAutoScroll
 *
 * 채팅 스크롤 자동 관리 훅
 * - 새 메시지 또는 스트리밍 청크 추가 시 자동으로 하단 스크롤
 * - 사용자가 위로 스크롤하면 자동 스크롤 중지 (Free Scroll 모드)
 * - Free Scroll 모드에서는 사용자가 명시적으로 해제할 때까지 자동 스크롤 안 함
 *
 * 원본: Tutor.js Lines 804-814, 861-864, 960-972
 */
export const useAutoScroll = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Zustand 스토어
  const { history, pieceDataSum, fetching } = useTutorChatStore();
  const { isFreeScroll, setIsFreeScroll } = useUIStore();
  const { currentGroupId } = useGroupListStore();

  // 서버에서 가져온 채팅 히스토리
  const { data: chatHistory } = useChatHistoryQuery({
    mode: Mode.TUTOR,
    groupId: currentGroupId,
  });

  /**
   * 스크롤을 최하단으로 이동
   */
  const scrollToBottom = useCallback((smooth: boolean = true) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }, []);

  /**
   * 스크롤 위치 감지
   * - 최하단에서 threshold 이상 벗어나면 Free Scroll 모드 활성화
   * - 최하단 근처면 Free Scroll 모드 해제
   */
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // 최하단에서 threshold 이상 벗어났을 때만 Free Scroll 활성화
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 100;

    if (distanceFromBottom > threshold) {
      setIsFreeScroll(true);
    } else {
      setIsFreeScroll(false);
    }
  }, [setIsFreeScroll]);

  /**
   * 스크롤 이벤트 리스너 등록
   */
  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  /**
   * 초기 마운트 시 스크롤 하단으로 이동
   */
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  /**
   * 메시지 추가 또는 스트리밍 청크 수신 시 자동 스크롤
   * - isFreeScroll이 false일 때만 실행
   * - history.length: 새 메시지 추가 감지
   * - pieceDataSum.length: 스트리밍 청크 추가 감지
   */
  useEffect(() => {
    if (!isFreeScroll) {
      scrollToBottom(true);
    }
  }, [history.length, pieceDataSum.length, isFreeScroll, scrollToBottom]);

  /**
   * 채팅 그룹 전환 시 스크롤 하단으로 이동
   * - currentGroupId가 변경될 때마다 실행
   * - Free Scroll 모드 해제하고 즉시 하단으로 스크롤
   */
  useEffect(() => {
    if (currentGroupId) {
      setIsFreeScroll(false);
      // 약간의 지연을 두어 DOM 업데이트 후 스크롤
      setTimeout(() => {
        scrollToBottom(false);
      }, 300);
    }
  }, [currentGroupId, setIsFreeScroll, scrollToBottom]);

  /**
   * Free Scroll 모드 해제 및 스크롤 이동
   */
  const disableFreeScrollAndScroll = () => {
    setIsFreeScroll(false);
    scrollToBottom(true);
  };

  return {
    scrollRef,
    scrollToBottom,
    disableFreeScrollAndScroll,
    isFreeScroll,
    fetching,
    hasMessages: history.length > 0 || (chatHistory?.length ?? 0) > 0,
  };
};
